import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type DensityMode = 'comfortable' | 'compact' | 'ultra-compact';

interface DensityModeContextType {
  // Estado reativo - força re-render quando muda
  densityCache: Map<string, DensityMode>;
  
  // Funções contextuais - usam chave única (userId + contextKey)
  getDensity: (userId: string, contextKey: string) => DensityMode;
  setDensity: (userId: string, contextKey: string, mode: DensityMode) => void;
  cycleDensity: (userId: string, contextKey: string) => void;
  
  // Helpers estáticos (não dependem de contexto)
  getLabelForMode: (mode: DensityMode) => string;
  getIconForMode: (mode: DensityMode) => string;
}

const DensityModeContext = createContext<DensityModeContextType | undefined>(undefined);

// Chave de localStorage: density-{userId}-{contextKey}
const getStorageKey = (userId: string, contextKey: string) => 
  `density-${userId}-${contextKey}`;

// VALOR PADRÃO: 'comfortable' (conforme requisito)
const DEFAULT_MODE: DensityMode = 'comfortable';

// Validar se é um modo válido
const isValidMode = (value: unknown): value is DensityMode => 
  typeof value === 'string' && ['comfortable', 'compact', 'ultra-compact'].includes(value);

export function DensityModeProvider({ children }: { children: ReactNode }) {
  // Estado reativo: Map de chave completa (userId-contextKey) -> modo
  // Mudanças neste Map disparam re-render nos consumidores
  const [densityCache, setDensityCache] = useState<Map<string, DensityMode>>(() => new Map());

  // Obter densidade para um contexto específico
  const getDensity = useCallback((userId: string, contextKey: string): DensityMode => {
    const fullKey = `${userId}-${contextKey}`;
    
    // 1. Verificar cache em memória (estado React)
    const cached = densityCache.get(fullKey);
    if (cached) return cached;
    
    // 2. Verificar localStorage
    const storageKey = getStorageKey(userId, contextKey);
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && isValidMode(saved)) {
        // Atualizar cache em memória (não dispara re-render aqui, será lazy)
        return saved;
      }
    } catch (e) {
      console.warn('[DensityMode] Erro ao ler localStorage:', e);
    }
    
    // 3. Retornar padrão: 'comfortable'
    return DEFAULT_MODE;
  }, [densityCache]);

  // Definir densidade para um contexto específico
  const setDensity = useCallback((userId: string, contextKey: string, mode: DensityMode) => {
    const storageKey = getStorageKey(userId, contextKey);
    const fullKey = `${userId}-${contextKey}`;
    
    // 1. Persistir em localStorage
    try {
      localStorage.setItem(storageKey, mode);
    } catch (e) {
      console.warn('[DensityMode] Erro ao salvar localStorage:', e);
    }
    
    // 2. Atualizar estado React (dispara re-render nos consumidores)
    setDensityCache(prev => {
      const next = new Map(prev);
      next.set(fullKey, mode);
      return next;
    });
  }, []);

  // Ciclar entre modos para um contexto específico
  const cycleDensity = useCallback((userId: string, contextKey: string) => {
    const current = getDensity(userId, contextKey);
    let next: DensityMode;
    
    if (current === 'comfortable') next = 'compact';
    else if (current === 'compact') next = 'ultra-compact';
    else next = 'comfortable';
    
    setDensity(userId, contextKey, next);
  }, [getDensity, setDensity]);

  // Helpers estáticos
  const getLabelForMode = useCallback((mode: DensityMode): string => {
    if (mode === 'comfortable') return 'Confortável';
    if (mode === 'compact') return 'Compacto';
    return 'Ultra-Compacto';
  }, []);

  const getIconForMode = useCallback((mode: DensityMode): string => {
    if (mode === 'comfortable') return '🔵';
    if (mode === 'compact') return '🟢';
    return '⚡';
  }, []);

  return (
    <DensityModeContext.Provider value={{
      densityCache,
      getDensity,
      setDensity,
      cycleDensity,
      getLabelForMode,
      getIconForMode
    }}>
      {children}
    </DensityModeContext.Provider>
  );
}

export function useDensityModeContext() {
  const context = useContext(DensityModeContext);
  if (!context) {
    throw new Error('useDensityModeContext must be used within DensityModeProvider');
  }
  return context;
}
